/**
 * Credential redaction for observability payloads (Sentry events, console args).
 *
 * Single source of truth for every logger fork — web (`apps/web/src/lib/logger.ts`),
 * Workers (`packages/runtime/src/logger.ts`), and mobile
 * (`apps/mobile/src/observability/scrub.ts`). They previously carried forked copies
 * of this heuristic, so a fix to one silently left the others exposed.
 *
 * Two independent passes, because a secret reaches an observability sink in two shapes:
 *   - as an object value → `sanitizeForSentry` walks keys (`SENSITIVE_FIELDS`)
 *   - as a bare string → the key walk can't see inside it, so `redactSecrets` matches
 *     on whole-string shape (opaque/base64 bodies) and on substring context (token
 *     prefixes, `"token":"…"`, `token=…`). A response body, an Authorization header,
 *     or a JWT arrives as one opaque string with no keys to walk.
 */

// --- Public identifiers (whole-string allowlist) -----------------------------
// hub_id, conversation_id, DO hex ids, SHA hashes, and trace/span ids are public
// and load-bearing for debugging, but are shaped like the secret heuristics below.
// Allowlisted first so `logger.error('orphan', { hex_id })` stays readable in Sentry.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// {16,} covers real identifiers (OTel span=16, trace=32, MD5=32, SHA-1=40,
// SHA-256 / CF DO hex=64) while excluding short hex-shaped values that could
// conceivably be secret fragments (e.g. 8-char API key prefixes).
const HEX_RE = /^[0-9a-f]{16,}$/;
// WorkOS principal ids (user_/org_ + 26-char Crockford-base32 ULID). Events carry
// `user_id` for attribution, and Sentry's "users impacted" counts
// it — redacting it made production impact uncountable. Secrets don't collide:
// way_ token bodies are longer and mixed-case base62 (ULID excludes lowercase and
// I/L/O/U), and key-based redaction still runs first.
const WORKOS_ID_RE = /^(?:user|org)_[0-9A-HJKMNP-TV-Z]{26}$/;
// Trace context headers: W3C `traceparent` (version-traceid-spanid-flags), which the web
// proxy forwards from the browser's Faro on every API call, and Sentry's `sentry-trace`
// (traceid-spanid, optional sampling flag). The same trace and span ids as above, joined by
// dashes, which HEX_RE does not take and the opaque rule below would redact.
const TRACE_HEADER_RE = /^(?:[0-9a-f]{2}-[0-9a-f]{32}-[0-9a-f]{16}-[0-9a-f]{2}|[0-9a-f]{32}-[0-9a-f]{16}(?:-[01])?)$/;

// Generated entity slugs carry a readable kebab prefix plus `--` and an
// eight-character stable suffix. They are public only when a structured field
// identifies them as an id; the same bare value remains credential-shaped and
// is redacted. The narrow shape prevents an arbitrary opaque token placed under
// an `_id` key from becoming a sanitizer bypass.
const GENERATED_SLUG_ID_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*--[a-z0-9]{8}$/;
const PUBLIC_ID_KEY_RE = /(?:^|_)id$/i;

function isPublicIdentifier(value: string): boolean {
  return UUID_RE.test(value) || HEX_RE.test(value) || WORKOS_ID_RE.test(value) || TRACE_HEADER_RE.test(value);
}

// --- Whole-string secret heuristics -----------------------------------------
// An opaque credential passed as the entire string: long, and drawn only from the
// alphabet tokens/keys use. Length ≥21 keeps ordinary identifiers readable.
const OPAQUE_SECRET_RE = /^[A-Za-z0-9_-]{21,}$/;

// Base64 bodies (wrapped DEKs, HMAC secrets, Google service-account material).
// Requires a `+` or `=` rather than accepting the bare base64 alphabet: without
// that marker this would also match slash-separated file paths and URL fragments,
// which are debugging context, not secrets. A base64 body that happens to contain
// neither is already caught by OPAQUE_SECRET_RE.
const BASE64_SECRET_RE = /^[A-Za-z0-9+/]{20,}={0,2}$/;
const BASE64_MARKER_RE = /[+=]/;

// --- Substring secret shapes -------------------------------------------------
// Prefix-anchored credential shapes, matched anywhere in a string. Deliberately
// conservative — each alternative is anchored to a literal prefix, so ordinary
// prose is never mangled.
const TOKEN_RE = new RegExp(
  [
    // WayAI API tokens and WebSocket tickets.
    '(?:way|wst)_[A-Za-z0-9._-]{8,}',
    // Authorization header values, including base64 Basic credentials.
    '(?:Bearer|Basic)\\s+[A-Za-z0-9._~+/=-]{8,}',
    // JWTs — WorkOS session/access tokens. `eyJ` is base64 for `{"`.
    'eyJ[A-Za-z0-9._-]{10,}',
    // Stripe secret/publishable/restricted keys and webhook signing secrets.
    '(?:sk|pk|rk)_(?:live|test)_[A-Za-z0-9]{8,}',
    'whsec_[A-Za-z0-9]{8,}',
    // Telegram bot tokens as the Bot API takes them, in the URL path: `/bot<id>:<secret>/`.
    'bot[0-9]{5,}:[A-Za-z0-9_-]{30,}',
    // NOTE: LLM provider keys are NOT here — they need a delimiter-preserving
    // boundary, so they live in PROVIDER_TOKEN_RE below.
  ].join('|'),
  'g',
);

/**
 * Field names whose value is a credential regardless of shape. Drives both the
 * object-key walk in `sanitizeForSentry` and the serialized-body patterns below,
 * so the two paths can't drift apart. Matched as a substring of the key, so
 * `access_token` is covered by `token`.
 */
const SENSITIVE_FIELDS = [
  'password',
  'token',
  'secret',
  'api_key',
  'apikey',
  'api-key',
  'authorization',
  'auth',
  'credential',
  'private_key',
  'privatekey',
  'access_token',
  'refresh_token',
  'session_id',
  'sessionid',
  'cookie',
  'jwt',
];

const SENSITIVE_KEY_ALTERNATION = SENSITIVE_FIELDS.map((field) =>
  field.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
).join('|');

// A credential name surrounded by any other key characters — mirrors the
// substring `includes` semantics of the object-key walk.
const SENSITIVE_KEY_PATTERN = `[A-Za-z0-9_.-]*(?:${SENSITIVE_KEY_ALTERNATION})[A-Za-z0-9_.-]*`;

// `"access_token": "…"` inside a serialized JSON body. The value pattern tolerates
// escaped quotes so a body can't terminate the match early and leak its tail.
const JSON_CREDENTIAL_RE = new RegExp(
  `("${SENSITIVE_KEY_PATTERN}"\\s*:\\s*)"(?:[^"\\\\]|\\\\.)*"`,
  'gi',
);

// `access_token=…` in a query string, form body, or OAuth redirect fragment.
const QUERY_CREDENTIAL_RE = new RegExp(
  `((?:^|[?&#;\\s])${SENSITIVE_KEY_PATTERN}=)[^&;\\s"']+`,
  'gi',
);

const REDACTED = '[REDACTED]';

const DATA_EXPLORER_KV_ACTIONS = new Set(['list', 'value', 'delete']);

// Data Explorer's legacy value/delete routes carry a KV key in the path and
// repeat it in DELETE's confirmation query. Request-level observability runs
// before route validation, so redact the whole identity at the shared string
// boundary without trying to classify which key shapes are secret. The current
// body-carrying endpoints use a finite action vocabulary; preserve only those
// exact segments so operational telemetry can still distinguish each command.
const DATA_EXPLORER_KV_KEY_TARGET =
  /(\/api\/admin\/data-explorer\/kv\/namespaces\/[^/?\s]+\/keys\/)([^?\s]+)(\?[^ \t\r\n]*)?/g;

/**
 * Redact sensitive identities embedded in an HTTP request target.
 *
 * This is deliberately stricter than the Data Explorer UI: every key on the
 * legacy key-in-path route is removed from generic observability, while the
 * route's explicit audit event retains safe actionable identities.
 */
export function redactRequestTarget(input: string): string {
  return input.replace(
    DATA_EXPLORER_KV_KEY_TARGET,
    (_match, prefix: string, segment: string, query?: string) => {
      if (DATA_EXPLORER_KV_ACTIONS.has(segment)) {
        return `${prefix}${segment}${query ? '?[REDACTED]' : ''}`;
      }
      return `${prefix}${encodeURIComponent(REDACTED)}${query ? '?[REDACTED]' : ''}`;
    },
  );
}

/**
 * LLM provider API keys. Note the HYPHEN — Stripe's `sk_live_` in TOKEN_RE is the
 * underscore family and does not cover these. One `sk-` pattern spans OpenAI (`sk-…`,
 * `sk-proj-…`), Anthropic (`sk-ant-api03-…`) and OpenRouter (`sk-or-v1-…`).
 *
 * These matter most of any shape in this module: every LLM adapter embeds the upstream
 * error BODY in its thrown message ("… API error: 401 - <body>"), and that message
 * becomes the Sentry issue title verbatim — captureException does not sanitize it. The
 * body those adapters cite in their own comments is literally "Incorrect API key
 * provided: sk-proj-…": a bare string with no key to walk, which is what this pass is for.
 *
 * Separate from TOKEN_RE because the leading boundary must be CAPTURED and put back,
 * which needs its own replacement. Two constraints force that shape:
 *
 *  - It must reject a preceding LETTER OR DIGIT, because these prefixes occur inside
 *    ordinary words: `sk-` sits in `task-`/`subtask-`/`disk-` and `xai-` in `maxai-`, so
 *    an unanchored rule redacted `task-abc123def456ghi789` to `ta[REDACTED]` — destroying
 *    a real identifier in Sentry, Loki and the persisted eval `error_message`.
 *  - It must NOT reject a preceding `_`, which is why plain `\b` is wrong here: `_` is a
 *    word character, so `\b` never fires between it and the prefix, and
 *    `provider_key_sk-proj-…` — a fully usable credential — sailed through.
 *
 * Lookbehind would express this directly but is avoided deliberately: `packages/core` is
 * RN-safe and Hermes has not reliably supported lookbehind assertions, so it could throw
 * at runtime on mobile.
 *
 * The 16-char floor is deliberate. Every provider key clears it by a wide margin (OpenAI
 * ~48+, Anthropic `sk-ant-api03-` + ~95, OpenRouter `sk-or-v1-` + 64), so nothing usable
 * slips under it, while short hyphenated prose (`sk-lite`, `sk-2`) stays untouched. A
 * fragment below the floor is a masked/truncated echo — identifying, but not usable.
 */
const PROVIDER_TOKEN_RE = new RegExp(
  '(^|[^A-Za-z0-9])(?:' +
    [
      'sk-[A-Za-z0-9_-]{16,}',
      'AIza[A-Za-z0-9_-]{16,}',
      'xai-[A-Za-z0-9_-]{16,}',
    ].join('|') +
    ')',
  'g',
);

/**
 * The data API's signed file URL token (`/file-urls/{org}/{base}/{token}`), a bearer credential for
 * its whole life, which wayai-backend serves and its agent tools fetch: `v<version>.<g|p>.<expiry>`
 * then two 43-character base64url parts. The data tree's own redaction uses the same shape
 * (`apps/rekor/shared/src/file-url-redaction.ts` `FILE_URL_TOKEN`; the two are pinned by
 * `apps/backend/src/data/env.test.ts`). No leading boundary: in a percent-encoded URL the token
 * follows `%2F`, a word character.
 */
const FILE_URL_TOKEN_RE = /v[0-9]+\.[gp]\.[0-9]+\.[A-Za-z0-9_-]{43}\.[A-Za-z0-9_-]{43}/g;

/**
 * `text` with every signed file URL token replaced, and nothing else changed: for a value that gets
 * no other secret pass (a span attribute such as `http.target`). `redactSecrets` includes it.
 */
export function redactFileUrlTokens(text: string): string {
  return text.replace(FILE_URL_TOKEN_RE, REDACTED);
}

/**
 * Redact credential-shaped substrings — token shapes, and credential values inside
 * serialized JSON / query strings. Internal: callers get `redactSecrets`, which
 * also applies the whole-string checks. Exporting this separately is how mobile
 * ended up wired to half the contract.
 */
function redactTokenShapes(input: string): string {
  return input
    .replace(FILE_URL_TOKEN_RE, REDACTED)
    .replace(TOKEN_RE, REDACTED)
    // `$1` puts the captured delimiter back — it is a boundary, not part of the secret.
    .replace(PROVIDER_TOKEN_RE, `$1${REDACTED}`)
    .replace(JSON_CREDENTIAL_RE, `$1"${REDACTED}"`)
    .replace(QUERY_CREDENTIAL_RE, `$1${REDACTED}`);
}

/**
 * Redact secrets from a bare string — the ONE string-redaction entry point.
 *
 * Allowlist public ids, then whole-string heuristics (an opaque or base64 body
 * passed as the entire value), then substring shapes (a secret carrying
 * punctuation — a JWT's dots, `Bearer `'s space, base64's `+/=` — which a
 * whole-string alphabet test cannot match, and which used to reach Sentry
 * verbatim).
 *
 * Deliberately the only exported general-purpose secret-string function: every
 * sink (Sentry `extra`, console args, the mobile event scrubber, Faro items)
 * must get all three passes. `redactRequestTarget` above is a narrow structural
 * scrubber for a route whose URL itself carries an identity.
 */
export function redactSecrets(value: string): string {
  if (isPublicIdentifier(value)) {
    return value;
  }
  if (OPAQUE_SECRET_RE.test(value)) {
    return REDACTED;
  }
  if (BASE64_SECRET_RE.test(value) && BASE64_MARKER_RE.test(value)) {
    return REDACTED;
  }
  return redactTokenShapes(value);
}

const MAX_SANITIZE_DEPTH = 10;

/** Sentinel: `data` is not one of the built-ins this module special-cases. */
const NOT_BUILTIN = Symbol('not-builtin');

/**
 * Capture a built-in's value rather than returning the object itself.
 *
 * Built-ins would otherwise walk to '{}' — their value lives in an internal
 * slot, not on Object.entries — but handing back the live instance is unsafe for
 * either sink. JSON.stringify and Sentry's normalize() resolve `toJSON`
 * dynamically and INVOKE it, so a replacement (own, inherited, getter-valued, or
 * a Proxy trap) could return values that never passed redaction; and anything
 * attached to the instance stays attached for the console to display — defeating
 * the credential-shaped-field auto-redaction this module exists to guarantee on
 * both the console and Sentry paths.
 *
 * Reading the value through intrinsics bound to internal slots sidesteps all of
 * that at once: a hijacked hook is never consulted, adornments are dropped
 * rather than detected (no enumerable/symbol/intrinsic-property census to keep
 * correct), and a receiver without those slots — a Proxy — fails closed instead
 * of throwing out of logger.error.
 */
function resolveBuiltin(data: object): unknown {
  try {
    if (data instanceof Error) {
      return {
        name: redactSecrets(data.name),
        message: redactSecrets(data.message),
        stack: typeof data.stack === 'string' ? redactSecrets(data.stack) : undefined,
      };
    }
    if (data instanceof Date) {
      const ms = Date.prototype.getTime.call(data);
      // null mirrors JSON.stringify(new Date('nope')) — toISOString would throw
      // RangeError, and a logger that throws swallows the error it was reporting.
      return Number.isFinite(ms) ? new Date(ms).toISOString() : null;
    }
    if (data instanceof RegExp) return RegExp.prototype.toString.call(data);
    if (typeof URL !== 'undefined' && data instanceof URL) {
      const safeUrl = new URL(URL.prototype.toString.call(data));
      safeUrl.username = '';
      safeUrl.password = '';
      return redactSecrets(URL.prototype.toString.call(safeUrl));
    }
    return NOT_BUILTIN;
  } catch {
    return '[UNSAFE_BUILTIN]';
  }
}

/**
 * Sanitize a value before it reaches Sentry or the console.
 *
 * Uses an ancestors stack rather than a global seen-set so diamond references —
 * the same object reached via two sibling paths — survive instead of being marked
 * '[CIRCULAR]'; only true ancestor cycles are collapsed. Depth is bounded to guard
 * against pathological trees.
 */
function sanitizeValue(
  data: unknown,
  ancestors: WeakSet<object>,
  depth: number,
  parentKey?: string,
): unknown {
  if (data === null || data === undefined) {
    return data;
  }

  // Functions must never survive onto the sanitized copy. Object.entries picks
  // up an own-enumerable `toJSON`, and both JSON.stringify and Sentry's
  // normalize() INVOKE it — so a closure-based one returns values that never
  // passed redaction, and a receiver-sensitive one throws out of logger.error.
  if (typeof data === 'function') {
    return '[Function]';
  }

  if (typeof data === 'string') {
    if (parentKey && PUBLIC_ID_KEY_RE.test(parentKey) && GENERATED_SLUG_ID_RE.test(data)) {
      return data;
    }
    return redactSecrets(data);
  }

  if (typeof data !== 'object') {
    return data;
  }

  // Built-in types whose useful representation isn't on Object.entries.
  const builtin = resolveBuiltin(data as object);
  if (builtin !== NOT_BUILTIN) {
    return builtin;
  }

  if (depth >= MAX_SANITIZE_DEPTH) {
    return '[MAX_DEPTH]';
  }
  if (ancestors.has(data as object)) {
    return '[CIRCULAR]';
  }
  ancestors.add(data as object);

  try {
    if (Array.isArray(data)) {
      return data.map((item) => sanitizeValue(item, ancestors, depth + 1));
    }

    const sanitized: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
      const lowerKey = key.toLowerCase();
      if (SENSITIVE_FIELDS.some((field) => lowerKey.includes(field))) {
        sanitized[key] = REDACTED;
      } else {
        sanitized[key] = sanitizeValue(value, ancestors, depth + 1, key);
      }
    }
    return sanitized;
  } catch {
    return '[UNSERIALIZABLE]';
  } finally {
    // Pop from the ancestors stack on the way out so siblings/diamonds
    // referencing the same node are not falsely marked as cycles.
    ancestors.delete(data as object);
  }
}

export function sanitizeForSentry(data: unknown): unknown {
  return sanitizeValue(data, new WeakSet(), 0);
}

/**
 * True when a string is a public identifier that must survive redaction — used by
 * the Sentry user-attribution path, which must not become a sanitizer bypass.
 */
export function isPublicIdShape(value: string): boolean {
  return WORKOS_ID_RE.test(value) || UUID_RE.test(value);
}
